import React, { Fragment, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";

import AdministrationHeader from "../../components/header-components/AdministrationHeader";
import FlexTable from "../../components/ui-components/FlexTable";
import TableCard from "../../components/ui-components/TableCard";
import EmptyState from "../../components/ui-components/EmptyState";
import StatusPill from "../Budgets/components/StatusPill";
import { listUsers } from "./api";

const STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "active", label: "Active" },
  { value: "invited", label: "Invited" },
  { value: "suspended", label: "Suspended" },
];

// ── Action buttons ──────────────────────────────────────────────

function ActionButtons({ user, onRowClick }) {
  return (
    <div className="flex items-center justify-center space-x-2">
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRowClick(user.id);
        }}
        className="text-lg px-2 text-text-2 hover:text-text hover:bg-surface-2 rounded-ctrl transition-colors cursor-pointer"
        title="View user"
      >
        <FontAwesomeIcon
          icon={icon({ name: "eye", style: "light" })}
          className="w-4 h-4"
        />
      </button>
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────

const UsersListPage = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [search, setSearch] = useState("");

  const debounceRef = React.useRef(null);
  const handleSearchChange = (val) => {
    setSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(val), 300);
  };

  const queryParams = useMemo(() => {
    const p = { limit: 50 };
    if (debouncedSearch) p.q = debouncedSearch;
    if (statusFilter) p.status = statusFilter;
    return p;
  }, [debouncedSearch, statusFilter]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["managed-users", queryParams],
    queryFn: () => listUsers(queryParams),
    keepPreviousData: true,
  });

  const users = data?.users?.items || [];

  const onRowClick = useCallback((id) => navigate(`/settings/users/${id}`), [navigate]);

  const columnDefs = useMemo(
    () => [
      // ── Name + email subtitle ──
      {
        accessor: "email",
        Header: () => <span>User</span>,
        Cell: ({ row }) => {
          const { first_name, last_name, email } = row.original;
          const hasName = first_name || last_name;
          return (
            <div className="flex flex-col gap-0.5 min-w-0">
              <span className="font-medium text-text truncate">
                {hasName ? `${first_name} ${last_name}`.trim() : email}
              </span>
              {hasName && (
                <span className="text-xs text-text-2 truncate">
                  {email}
                </span>
              )}
            </div>
          );
        },
        flex: 1,
        minWidth: 240,
      },

      // ── Function ──
      {
        id: "function",
        accessor: "function",
        Header: () => <span>Function</span>,
        Cell: ({ row }) => (
          <span className="text-text truncate block max-w-[200px]">
            {row.original.function || "—"}
          </span>
        ),
        width: 180,
        minWidth: 120,
        maxWidth: 260,
      },

      // ── Roles ──
      {
        id: "roles",
        Header: () => <span>Roles</span>,
        Cell: ({ row }) => (
          <div className="flex gap-1.5 flex-wrap">
            {(row.original.roles || []).map((r) => (
              <span
                key={r.code}
                className="bg-surface-2 text-text px-2 py-0.5 rounded text-sm border border-border"
              >
                {r.name}
              </span>
            ))}
          </div>
        ),
        width: 220,
        minWidth: 180,
        maxWidth: 300,
      },

      // ── Status ──
      {
        accessor: "status",
        align: "center",
        Header: () => <span>Status</span>,
        Cell: ({ value }) => (
          <StatusPill status={value?.toLowerCase()} />
        ),
        width: 120,
        minWidth: 110,
        maxWidth: 140,
      },

      // ── Keycloak ──
      {
        id: "keycloak",
        align: "center",
        Header: () => <span>Keycloak</span>,
        Cell: ({ row }) =>
          row.original.keycloak_linked ? (
            <span className="inline-flex items-center gap-1.5 text-success">
              <FontAwesomeIcon
                icon={icon({ name: "link", style: "light" })}
                className="w-3.5 h-3.5"
              />
              Linked
            </span>
          ) : (
            <span className="text-text-2">Not linked</span>
          ),
        width: 110,
        minWidth: 100,
        maxWidth: 140,
      },

      // ── Actions ──
      {
        id: "actions",
        align: "center",
        Header: () => <span>Actions</span>,
        Cell: ({ row }) => (
          <ActionButtons user={row.original} onRowClick={onRowClick} />
        ),
        width: 100,
        minWidth: 80,
        maxWidth: 120,
      },
    ],
    [onRowClick]
  );

  const statusLabel = statusFilter || "all";
  const hint = isLoading
    ? "Loading…"
    : `Showing ${users.length} ${statusLabel} ${users.length === 1 ? "user" : "users"}`;

  return (
    <Fragment>
      <AdministrationHeader title="User Management" className="text-text-3" showPeriodSelector={false} />
      <div className="w-full p-8">
        <TableCard
          title="Users"
          subtitle={hint}
          primaryAction={{
            label: "Invite User",
            onClick: () => navigate("/settings/users/new"),
            icon: icon({ name: "plus", style: "regular" }),
          }}
          searchValue={search}
          onSearchChange={handleSearchChange}
          searchPlaceholder="Search by name or email…"
          filterValue={statusFilter}
          onFilterChange={setStatusFilter}
          filterOptions={STATUS_OPTIONS}
          filterAriaLabel="Filter by status"
          isLoading={isLoading}
          error={error}
          errorMessage="Error loading users"
          onRetry={() => refetch()}
        >
          {users.length === 0 ? (
            statusFilter === "suspended" ? (
              <EmptyState
                icon={icon({ name: "user-slash", style: "light" })}
                title="No suspended users"
                message="Suspended users will appear here."
              />
            ) : (
              <EmptyState
                icon={icon({ name: "users", style: "light" })}
                title="No users yet"
                message="Invite a user to get started."
                action={{
                  label: "Invite user",
                  onClick: () => navigate("/settings/users/new"),
                  icon: icon({ name: "plus", style: "regular" }),
                }}
              />
            )
          ) : (
            <FlexTable
              columns={columnDefs}
              data={users}
              onRowClick={onRowClick}
              getRowId={(u) => u.id}
              rowAriaLabel={(u) => `View user ${u.email}`}
            />
          )}
        </TableCard>
      </div>
    </Fragment>
  );
};

export default UsersListPage;
